import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
interface ScheduleCallButtonProps {
  variant?: 'primary' | 'secondary';
}
export const ScheduleCallButton: React.FC<ScheduleCallButtonProps> = ({
  variant = 'primary'
}) => {
  const {
    language
  } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [message, setMessage] = useState('');
  const translations = {
    en: {
      buttonText: 'Schedule a Call',
      title: 'Schedule a Meeting',
      subtitle: 'Fill in your details and pick a time that works for you',
      nameLabel: 'Your Name *',
      namePlaceholder: 'John Doe',
      emailLabel: 'Your Email *',
      emailPlaceholder: 'you@company.com',
      dateLabel: 'Date *',
      timeLabel: 'Time *',
      timeSelect: 'Select',
      messageLabel: 'Message (Optional)',
      messagePlaceholder: 'What would you like to discuss?',
      submitButton: 'Add to Google Calendar',
      disclaimer: 'This will open Google Calendar with pre-filled details'
    },
    vi: {
      buttonText: 'Đặt Lịch Gọi',
      title: 'Đặt Lịch Họp',
      subtitle: 'Điền thông tin và chọn thời gian phù hợp với bạn',
      nameLabel: 'Tên của bạn *',
      namePlaceholder: 'Nguyễn Văn A',
      emailLabel: 'Email của bạn *',
      emailPlaceholder: 'ban@congty.com',
      dateLabel: 'Ngày *',
      timeLabel: 'Giờ *',
      timeSelect: 'Chọn',
      messageLabel: 'Tin nhắn (Tùy chọn)',
      messagePlaceholder: 'Bạn muốn thảo luận về điều gì?',
      submitButton: 'Thêm vào Google Calendar',
      disclaimer: 'Điều này sẽ mở Google Calendar với thông tin đã điền sẵn'
    }
  };
  const t = translations[language];
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour <= 17; hour++) {
      for (let minute of [0, 30]) {
        if (hour === 17 && minute === 30) break;
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        slots.push({
          value: time,
          label: displayTime
        });
      }
    }
    return slots;
  };
  const timeSlots = generateTimeSlots();
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name || !selectedDate || !selectedTime) {
      alert('Please fill in all required fields');
      return;
    }
    const dateObj = new Date(selectedDate + 'T' + selectedTime);
    const endDate = new Date(dateObj.getTime() + 30 * 60000);
    const formatGoogleDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    const startTime = formatGoogleDate(dateObj);
    const endTime = formatGoogleDate(endDate);
    const calendarUrl = new URL('https://calendar.google.com/calendar/render');
    calendarUrl.searchParams.append('action', 'TEMPLATE');
    calendarUrl.searchParams.append('text', `Meeting with ${name}`);
    calendarUrl.searchParams.append('dates', `${startTime}/${endTime}`);
    calendarUrl.searchParams.append('details', `Meeting scheduled via portfolio website.\n\nContact: ${email}\n\nMessage: ${message || 'No additional message'}`);
    calendarUrl.searchParams.append('add', `${email},officialtrungnguyen@gmail.com`);
    window.open(calendarUrl.toString(), '_blank');
    setIsOpen(false);
    setEmail('');
    setName('');
    setSelectedDate('');
    setSelectedTime('');
    setMessage('');
  };
  const buttonClasses = variant === 'primary' ? 'px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-full shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2 transition-all hover:scale-105 active:scale-95' : 'px-6 py-3 bg-secondary/50 backdrop-blur-sm border border-accent-cyan/30 rounded-full font-semibold text-primary hover:bg-secondary hover:border-accent-cyan transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center gap-2';
  const modalContent = isOpen ? <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'white',
    zIndex: 999999,
    overflowY: 'auto',
    padding: '4rem 2rem'
  }} data-id="element-1149">
      {/* Close button */}
      <button onClick={() => setIsOpen(false)} style={{
      position: 'fixed',
      top: '2rem',
      right: '2rem',
      padding: '0.75rem',
      borderRadius: '9999px',
      backgroundColor: '#f3f4f6',
      border: 'none',
      cursor: 'pointer',
      zIndex: 1000000
    }} data-id="element-1150">
        <X size={24} color="#4b5563" data-id="element-1151" />
      </button>

      {/* Header */}
      <div style={{
      textAlign: 'center',
      marginBottom: '4rem'
    }} data-id="element-1152">
        <h2 style={{
        fontSize: '3.75rem',
        fontWeight: 'bold',
        color: '#111827',
        marginBottom: '1rem'
      }} data-id="element-1153">
          {t.title}
        </h2>
        <p style={{
        fontSize: '1.5rem',
        color: '#6b7280'
      }} data-id="element-1154">
          {t.subtitle}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} style={{
      maxWidth: '56rem',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '2.5rem'
    }} data-id="element-1155">
        {/* Name field */}
        <div data-id="element-1156">
          <label style={{
          display: 'block',
          fontSize: '1.5rem',
          fontWeight: '600',
          color: '#374151',
          marginBottom: '1rem'
        }} data-id="element-1157">
            {t.nameLabel}
          </label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder={t.namePlaceholder} required style={{
          width: '100%',
          padding: '1.5rem 2rem',
          fontSize: '1.5rem',
          backgroundColor: '#f9fafb',
          border: '2px solid #d1d5db',
          borderRadius: '1rem',
          color: '#111827'
        }} data-id="element-1158" />
        </div>

        {/* Email field */}
        <div data-id="element-1159">
          <label style={{
          display: 'block',
          fontSize: '1.5rem',
          fontWeight: '600',
          color: '#374151',
          marginBottom: '1rem'
        }} data-id="element-1160">
            {t.emailLabel}
          </label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t.emailPlaceholder} required style={{
          width: '100%',
          padding: '1.5rem 2rem',
          fontSize: '1.5rem',
          backgroundColor: '#f9fafb',
          border: '2px solid #d1d5db',
          borderRadius: '1rem',
          color: '#111827'
        }} data-id="element-1161" />
        </div>

        {/* Date and Time row */}
        <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '2rem'
      }} data-id="element-1162">
          <div data-id="element-1163">
            <label style={{
            display: 'block',
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '1rem'
          }} data-id="element-1164">
              {t.dateLabel}
            </label>
            <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} min={getMinDate()} required style={{
            width: '100%',
            padding: '1.5rem 2rem',
            fontSize: '1.5rem',
            backgroundColor: '#f9fafb',
            border: '2px solid #d1d5db',
            borderRadius: '1rem',
            color: '#111827'
          }} data-id="element-1165" />
          </div>

          <div data-id="element-1166">
            <label style={{
            display: 'block',
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '1rem'
          }} data-id="element-1167">
              {t.timeLabel}
            </label>
            <select value={selectedTime} onChange={e => setSelectedTime(e.target.value)} required style={{
            width: '100%',
            padding: '1.5rem 2rem',
            fontSize: '1.5rem',
            backgroundColor: '#f9fafb',
            border: '2px solid #d1d5db',
            borderRadius: '1rem',
            color: '#111827'
          }} data-id="element-1168">
              <option value="" data-id="element-1169">{t.timeSelect}</option>
              {timeSlots.map(slot => <option key={slot.value} value={slot.value} data-id="element-1170">
                  {slot.label}
                </option>)}
            </select>
          </div>
        </div>

        {/* Message field */}
        <div data-id="element-1171">
          <label style={{
          display: 'block',
          fontSize: '1.5rem',
          fontWeight: '600',
          color: '#374151',
          marginBottom: '1rem'
        }} data-id="element-1172">
            {t.messageLabel}
          </label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder={t.messagePlaceholder} rows={5} style={{
          width: '100%',
          padding: '1.5rem 2rem',
          fontSize: '1.5rem',
          backgroundColor: '#f9fafb',
          border: '2px solid #d1d5db',
          borderRadius: '1rem',
          color: '#111827',
          resize: 'none'
        }} data-id="element-1173" />
        </div>

        {/* Disclaimer */}
        <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1.5rem',
        padding: '2rem',
        backgroundColor: '#eff6ff',
        border: '2px solid #bfdbfe',
        borderRadius: '1rem'
      }} data-id="element-1174">
          <Calendar size={32} color="#2563eb" style={{
          flexShrink: 0,
          marginTop: '0.25rem'
        }} data-id="element-1175" />
          <p style={{
          fontSize: '1.25rem',
          color: '#374151'
        }} data-id="element-1176">
            {t.disclaimer}
          </p>
        </div>

        {/* SUBMIT BUTTON */}
        <button type="submit" style={{
        width: '100%',
        padding: '2rem 3rem',
        background: 'linear-gradient(to right, #2563eb, #4f46e5)',
        color: 'white',
        fontWeight: 'bold',
        fontSize: '2rem',
        borderRadius: '1rem',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.5rem',
        boxShadow: '0 20px 25px -5px rgba(37, 99, 235, 0.4)'
      }} data-id="element-1177">
          <Calendar size={36} data-id="element-1178" />
          <span data-id="element-1179">{t.submitButton}</span>
          <ArrowRight size={36} data-id="element-1180" />
        </button>
      </form>
    </div> : null;
  return <>
      <button onClick={() => setIsOpen(true)} className={buttonClasses} data-id="element-1181">
        <Calendar size={20} data-id="element-1182" />
        {t.buttonText}
      </button>

      {modalContent && createPortal(modalContent, document.body)}
    </>;
};